import { Close } from '@mui/icons-material'
import { Breakpoint, Typography, useMediaQuery } from '@mui/material'
import hotToast, { Renderable } from 'react-hot-toast'
import { ElementContent } from 'react-markdown/lib/ast-to-react'
import voca from 'voca'
import { Feature, FeatureCollection, Point } from 'geojson'

import { theme } from '../styles/universal'
import { PickerItem } from '../entities'
import { store } from '../store'
import { updateCurrentLocation } from '../slices/ProfileSlice'
import { getEnvVars } from '../config'
import axios, { AxiosResponse } from 'axios'
import { differenceInMinutes, differenceInWeeks } from 'date-fns'
import { getLocalItem, removeLocalItem, setLocalItem } from '../browser-storage'
import { TZDate } from '@date-fns/tz'

/**
 * Matches with windows larger than or equal to the given breakpoint
 * @param size Breakpoint
 * @returns boolean
 */
export const useWindowSizeUp = (size: Breakpoint): boolean => {
    return useMediaQuery(theme.breakpoints.up(size))
}

/**
 * Matches with windows smaller than or equal to the given breakpoint
 * @param size Breakpoint
 * @returns boolean
 */
export const useWindowSizeDown = (size: Breakpoint): boolean => {
    return useMediaQuery(theme.breakpoints.down(size))
}

/**
 * Matches with windows exactly the given screen size threshold
 * @param size Breakpoint
 * @returns boolean
 */
export const useWindowSizeExact = (size: Breakpoint): boolean => {
    return useMediaQuery(theme.breakpoints.only(size))
}

/**
 * Matches with windows between the given the breakpoints
 * @param size Breakpoint
 * @returns boolean
 */
export const useWindowSizeBetween = (
    start: Breakpoint,
    end: Breakpoint
): boolean => {
    return useMediaQuery(theme.breakpoints.between(start, end))
}

/**
 * Display a toast message
 * @param message string
 * @param icon React.ReactNode
 */
export const toast = (message: string, icon?: Renderable): void => {
    hotToast(
        (t) => (
            <Typography
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width={300}
                maxWidth="80vw"
                p={0}
            >
                {message}
                <Close
                    color="secondary"
                    sx={{ cursor: 'pointer', marginLeft: '1rem' }}
                    onClick={() => hotToast.dismiss(t.id)}
                />
            </Typography>
        ),
        {
            icon,
        }
    )
}

export const transformLinkUri = (
    href: string,
    _children: ElementContent[],
    _title: string | null
): string => {
    return voca.lowerCase(href.replace('hazadapt://', '/'))
}

export const getAddress = async (
    location: GeolocationPosition
): Promise<Feature<Point>> => {
    const { geocodingApiKey } = getEnvVars()
    const { latitude, longitude } = location.coords
    const reverseGeocodingEndpoint: string = `https://api.mapbox.com/search/geocode/v6/reverse?latitude=${latitude}&longitude=${longitude}&access_token=${geocodingApiKey}`
    const response: AxiosResponse<FeatureCollection<Point>> = await axios.get(
        reverseGeocodingEndpoint
    )
    const addressFeature = response.data.features.find(
        (f) =>
            f.properties?.feature_type === 'address' ||
            f.properties?.feature_type === 'street'
    )
    if (!addressFeature || !addressFeature.properties?.full_address)
        throw new Error('No address found')
    return addressFeature
}

export const getCurrentLocation = (skip = false) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
        if (skip) reject('Skipped location retrieval')
        else {
            navigator.geolocation.getCurrentPosition(
                async (location) => {
                    // Update reverse-geocoded ZIP if last retrieved more than 5 minutes ago
                    const currentZip =
                        store.getState().profile.currentLocation?.zip
                    const currentLocationLastUpdatedStr =
                        store.getState().profile.currentLocationLastUpdated
                    const currentLocationLastUpdated =
                        currentLocationLastUpdatedStr
                            ? new Date(currentLocationLastUpdatedStr)
                            : undefined
                    if (
                        !currentZip ||
                        !currentLocationLastUpdated ||
                        differenceInMinutes(
                            currentLocationLastUpdated,
                            new Date()
                        ) >= 5
                    ) {
                        const data = await getAddress(location)
                        store.dispatch(
                            updateCurrentLocation({
                                zip: data.properties?.context.postcode.name,
                                county:
                                    data.properties?.context.district &&
                                    data.properties?.context.region
                                        ? `${data.properties.context.district.name}, ${data.properties.context.region.region_code}`
                                        : undefined,
                                state: data.properties?.context.region.name,
                            })
                        )
                    }

                    resolve(location)
                },
                (err) => {
                    console.error(err)
                    return reject(err)
                },
                {
                    timeout: 3000,
                    maximumAge: 60000,
                    enableHighAccuracy: false,
                }
            )
        }
    })

/**
 * Sort the items in a picker alphabetically
 * @param a first item to compare
 * @param b second item to compare
 * @returns number
 */
export const sortPickerItemsAlphabetically = (
    a: PickerItem<any>,
    b: PickerItem<any>
) => {
    return a.label < b.label ? -1 : a.label > b.label ? 1 : 0
}

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} string
 * @param {Array} noSplit do not split words at the specified character (ex. dashes in hyphenated names)
 * @returns {string}
 */
export const titleCaseString = (string: string, noSplit?: []) => {
    return voca.titleCase(string, noSplit)
}

export const doesNeedZip = (zip: string | null | undefined) =>
    !zip || zip === 'undefined'

const NUM_TIMES_APP_OPENED_WITH_BAD_ZIP_KEY = 'hazadapt_zip_alert_counter'
const LAST_SHOWN_ZIP_ALERT_KEY = 'hazadapt_last_zip_alert'
const COUNT_INTERVAL_FOR_ZIP_ALERT = 5
const WEEKS_INTERVAL_FOR_ZIP_ALERT = 2

export const shouldShowZipAlert = (zip: string | null | undefined): boolean => {
    if (!doesNeedZip(zip)) return false
    const lastShownZipAlertStr = getLocalItem(LAST_SHOWN_ZIP_ALERT_KEY)

    // Default to hiding the alert
    let show = false

    if (!lastShownZipAlertStr) {
        // Has never shown the alert yet for this user; show it
        show = true
    } else {
        const lastShownZipAlert = new TZDate(lastShownZipAlertStr)
        const now = new TZDate()
        if (
            differenceInWeeks(now, lastShownZipAlert) >
            WEEKS_INTERVAL_FOR_ZIP_ALERT
        ) {
            // Last time the alert was shown was at least 2 weeks ago
            show = true
        } else {
            const numTimesAppOpenedWithBadZipStr = getLocalItem(
                NUM_TIMES_APP_OPENED_WITH_BAD_ZIP_KEY
            )
            if (
                numTimesAppOpenedWithBadZipStr &&
                voca.isNumeric(numTimesAppOpenedWithBadZipStr)
            ) {
                const numTimesAppOpenedWithBadZip = Math.max(
                    0,
                    parseInt(numTimesAppOpenedWithBadZipStr, 10)
                )
                if (numTimesAppOpenedWithBadZip === 0)
                    // The user has opened the app at least 5 times since last being shown the alert
                    show = true
                else {
                    // Not time to show the alert yet; increment the counter by 1
                    setLocalItem(
                        NUM_TIMES_APP_OPENED_WITH_BAD_ZIP_KEY,
                        String((numTimesAppOpenedWithBadZip + 1) %
                            COUNT_INTERVAL_FOR_ZIP_ALERT)
                    )
                }
            }
        }
    }

    if (show) {
        // Update ZIP alert trackers
        setLocalItem(LAST_SHOWN_ZIP_ALERT_KEY, new TZDate().toISOString())
        setLocalItem(NUM_TIMES_APP_OPENED_WITH_BAD_ZIP_KEY, '1')
    }
    return show
}

export const resetZipAlertTrackers = async () => {
    removeLocalItem(NUM_TIMES_APP_OPENED_WITH_BAD_ZIP_KEY)
    removeLocalItem(LAST_SHOWN_ZIP_ALERT_KEY)
}
